import { useState, useEffect } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { useToast } from '../../context/ToastContext';
import { adminAPI } from '../../utils/api';

const AdminApprovalsPage = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const { success, error } = useToast();

  useEffect(() => { loadPending(); }, []);

  const loadPending = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getPending({ limit: 50, registration_path: filter === 'all' ? undefined : filter });
      setStudents(res.data.data || []);
    } catch { setStudents([]); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id, name) => {
    try { await adminAPI.approveStudent(id); success(`${name} approved!`); loadPending(); }
    catch { error('Failed.'); }
  };

  const handleReject = async (id, name) => {
    const reason = prompt(`Reason for rejecting ${name}? (optional)`);
    try { await adminAPI.rejectStudent(id, reason || ''); success(`${name} rejected.`); loadPending(); }
    catch { error('Failed.'); }
  };

  const handleBulkApprove = async () => {
    if (!selected.length) return error('Select students first.');
    if (!window.confirm(`Approve ${selected.length} students?`)) return;
    try {
      await adminAPI.bulkApprove({ ids: selected });
      success(`${selected.length} students approved!`);
      setSelected([]);
      loadPending();
    } catch { error('Bulk approve failed.'); }
  };

  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = () => setSelected(selected.length === students.length ? [] : students.map(s => s.id));

  return (
    <AdminShell
      title="Pending Approvals"
      subtitle={`${students.length} students waiting for approval`}
      actions={
        <div style={{ display: 'flex', gap: '8px' }}>
          {selected.length > 0 && (
            <button onClick={handleBulkApprove} style={{ background: '#1B7F3E', color: 'white', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}>
              ✓ Approve Selected ({selected.length})
            </button>
          )}
          <button onClick={loadPending} style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>🔄 Refresh</button>
        </div>
      }
    >
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '16px' }}>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {[['all', 'All'], ['individual', 'Individual'], ['school', 'School']].map(([val, lbl]) => (
            <button key={val} onClick={() => { setFilter(val); loadPending(); }}
              style={{ padding: '5px 14px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, border: filter === val ? 'none' : '1px solid #E2E8F0', background: filter === val ? '#1A3C6E' : 'white', color: filter === val ? 'white' : '#64748B', cursor: 'pointer' }}>
              {lbl}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
        ) : students.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#64748B' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
            <div style={{ fontWeight: 700, fontSize: '16px' }}>No pending approvals!</div>
            <div style={{ fontSize: '13px', marginTop: '6px' }}>All students have been reviewed.</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid #E2E8F0' }}>
                  <input type="checkbox" checked={selected.length === students.length && students.length > 0} onChange={toggleAll} />
                </th>
                {['Student', 'Parent', 'Class', 'Location', 'Type', 'Registered', 'Actions'].map(h => (
                  <th key={h} style={{ fontSize: '10px', fontWeight: 800, color: '#64748B', textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E2E8F0', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {students.map(s => (
                <tr key={s.id} style={{ borderBottom: '1px solid #F7F8FC' }}>
                  <td style={{ padding: '10px 8px' }}>
                    <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggleSelect(s.id)} />
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#E8F0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, color: '#1A3C6E', flexShrink: 0 }}>
                        {s.name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </div>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 700 }}>{s.name}</div>
                        <div style={{ fontSize: '11px', color: '#64748B' }}>{s.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '10px 8px', fontSize: '12px', color: '#64748B' }}>
                    <div style={{ fontWeight: 600 }}>{s.parent?.name || '—'}</div>
                    <div style={{ fontSize: '11px' }}>{s.parent?.email || '—'}</div>
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 800, background: '#E8F0FB', color: '#1A3C6E', borderRadius: '20px', padding: '2px 10px' }}>Class {s.class_id}</span>
                  </td>
                  <td style={{ padding: '10px 8px', fontSize: '12px', color: '#64748B' }}>
                    {s.parent?.city ? `${s.parent.city}, ${s.parent.state}` : '—'}
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <span style={{ fontSize: '10px', fontWeight: 800, background: s.registration_path === 'school' ? '#FEF3C7' : '#E8F0FB', color: s.registration_path === 'school' ? '#7C2D12' : '#1A3C6E', borderRadius: '20px', padding: '2px 8px' }}>
                      {s.registration_path === 'school' ? '🏫 School' : '👤 Individual'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 8px', fontSize: '11px', color: '#64748B' }}>
                    {new Date(s.created_at).toLocaleDateString('en-IN')}
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button onClick={() => handleApprove(s.id, s.name)} style={{ background: '#1B7F3E', color: 'white', border: 'none', borderRadius: '20px', padding: '4px 10px', fontSize: '11px', fontWeight: 800, cursor: 'pointer' }}>✓ Approve</button>
                      <button onClick={() => handleReject(s.id, s.name)} style={{ background: '#FEE2E2', color: '#DC2626', border: '1px solid #DC2626', borderRadius: '20px', padding: '4px 9px', fontSize: '11px', fontWeight: 800, cursor: 'pointer' }}>✕ Reject</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminApprovalsPage;
